/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { useSearchParams } from 'react-router-dom'
import { debounce } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useHandleSearchParams } from 'app/utils'
import { debug } from 'lib/logging'

export function Search({ onChange }: { onChange?: (val: string) => void }): React.ReactElement {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [searchValue, setSearchValue] = useState(searchParams.get(`query`) ?? ``)
  // Bumped only when an external `query` change (one this component did not
  // just write itself) is detected, to key a remount of the uncontrolled
  // input below. Kept separate from `searchValue`'s own text: if the key were
  // `searchValue` itself, resetting back to a value that was already
  // rendered once (e.g. clearing back to `""` right after mount) would leave
  // the key unchanged, React would bail out of the `setState`, and the
  // remount would silently no-op, leaving stale typed text in the DOM.
  const [resetCount, setResetCount] = useState(0)
  // The text this component believes the DOM currently shows: either the
  // last value it wrote to the URL, or, when the 1-2 char guard below skips
  // that write, the last value it saw typed without writing it out. Tracking
  // "last written to the URL" instead would go stale on a guarded keystroke
  // (e.g. typing "ab") and stay stuck at an old value, so a later external
  // reset back to that same old value (e.g. the empty state's Clear button
  // resetting `query` to `""`) would be mistaken for its own echo and the
  // input would never remount. Lets the effect below tell its own change
  // apart from a genuine external one, so only the latter bumps `resetCount`.
  // Without this, every debounced keystroke would remount the input
  // mid-typing and steal focus.
  const lastSeen = useRef(searchParams.get(`query`) ?? ``)
  const handleSearchChange = (e) => {
    debug(`search`, `change`, e.target.value)
    if (onChange) {
      // NOTE: this branch never touches `lastSeen` or the URL, so an
      // external reset can't be told apart from this component's own change
      // in this mode. No current caller passes `onChange`; fix this if one
      // starts to.
      onChange(e.target.value)
    } else {
      lastSeen.current = e.target.value
      if (e?.target?.value?.length && e?.target?.value?.length < 3) return
      handleSearchParams({ query: e.target.value })
    }
  }

  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const next = searchParams.get(`query`) ?? ``
    if (next === lastSeen.current) return
    lastSeen.current = next
    setSearchValue(next)
    setResetCount((count) => count + 1)
  }, [searchParams])

  return (
    <div className="group">
      <div className="btn-sm btn-tertiary flex h-8 w-[180px] items-center justify-start md:w-[250px] @max-xl/toolbar:md:w-[150px]">
        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 cursor-pointer text-default" />

        <input
          // Remount on an external change to `query` (for example the Clear
          // button in the empty state) so the uncontrolled defaultValue is
          // re-read. Typing itself never bumps `resetCount`: `lastSeen`
          // above lets the effect recognize its own keystroke echoing back
          // through the URL and skip it, so a mid-debounce update never
          // steals focus. A fully controlled input would fight the 250ms
          // debounce on every keystroke instead.
          key={resetCount}
          ref={input}
          className="w-full border-none bg-transparent px-0 pl-1 text-body-sm text-default placeholder:text-subtle focus:border-none focus:ring-0 focus:ring-transparent"
          type="search"
          name="table-search"
          placeholder="Search"
          defaultValue={searchValue ?? ``}
          onChange={debounce((event) => handleSearchChange(event), 250)}
          autoComplete="off"
        />
      </div>
    </div>
  )
}
